import { Dialog, PlainButton } from "@jugl-web/ui-kit";
import { useTranslations } from "@jugl-web/utils";
import { ReactComponent as CloseIcon } from "@jugl-web/ui-kit/overlay/DrawerHeader/assets/close.svg";
import { useClipboard } from "@jugl-web/utils/hooks/useClipboard";
import { useInvoiceTypeToLabelDictionary } from "@jugl-web/rest-api/invoices/hooks/useInvoiceTypeToLabelDictionary";
import { InvoiceModel, InvoicesConfigModel } from "@jugl-web/rest-api";
import {
  InvoicePreview,
  InvoicePreviewHandle,
} from "@jugl-web/domain-resources/invoices/components/InvoicePreview";
import { useRef } from "react";
import {
  InvoicesTableActions,
  InvoicesTableNavigateFn,
  InvoicesAvailableAction,
} from "@jugl-web/domain-resources/invoices/types";
import { PreviewActions } from "./components/PreviewActions";
import { ReactComponent as DashboardIcon } from "./assets/dashboard.svg";

type PreviewInvoiceDialogProps = {
  entityName: string;
  globalConfig: InvoicesConfigModel | undefined;
  isOpen: boolean;
  onClose: () => void;
  invoiceData: Partial<InvoiceModel>;
  actions: InvoicesTableActions;
  availableActions: InvoicesAvailableAction[];
  displayPlace?: "table" | "item-form";
  onActionSuccess?: (
    action: "edit" | "void-invoice" | "convert",
    invoice?: InvoiceModel
  ) => void;
  entityLogo?: string;
  isReadOnly?: boolean;
  onNavigate?: InvoicesTableNavigateFn;
};

export const PreviewInvoiceDialog = ({
  entityName,
  entityLogo,
  globalConfig,
  isOpen,
  onClose,
  invoiceData,
  displayPlace = "item-form",
  onActionSuccess,
  isReadOnly = false,
  actions,
  onNavigate,
  availableActions,
}: PreviewInvoiceDialogProps) => {
  const { copyToClipboard } = useClipboard();
  const invoicePreviewRef = useRef<InvoicePreviewHandle>(null);
  const { t } = useTranslations();
  const dict = useInvoiceTypeToLabelDictionary();

  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={() => {}}
      size="auto"
      className="mt-6 w-[1420px] max-w-[80vw] bg-grey-100"
    >
      <>
        <header className="rounded-t-3xl bg-white">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center gap-2 font-secondary text-lg font-semibold text-dark">
              <DashboardIcon />
              {`${t({
                id: "common.preview",
                defaultMessage: "Preview",
              })} ${dict[invoiceData?.type ?? "invoice"]}`}
            </div>
            <PlainButton onClick={handleClose}>
              <CloseIcon />
            </PlainButton>
          </div>
          <div className="h-px w-full bg-dark-100" />
        </header>

        <div className="jugl__custom-scrollbar max-h-[calc(100vh-17rem)] overflow-y-auto">
          <div className="mx-auto my-4 max-w-[760px] rounded-xl border border-solid border-grey-300 bg-white p-8">
            <InvoicePreview
              logo={globalConfig?.logo || entityLogo || ""}
              invoice={invoiceData}
              entityName={entityName}
              ref={invoicePreviewRef}
            />
          </div>
        </div>
        {invoiceData.id && (
          <PreviewActions
            onPrint={() => invoicePreviewRef.current?.download()}
            onCopyUrl={() => copyToClipboard(invoiceData.short_url || "")}
            invoiceId={invoiceData.id}
            status={invoiceData?.status}
            type={invoiceData?.type}
            displayPlace={displayPlace}
            onActionSuccess={onActionSuccess}
            isReadOnly={isReadOnly}
            actions={actions}
            onNavigate={onNavigate}
            availableActions={availableActions}
          />
        )}
      </>
    </Dialog>
  );
};
